'use client'

import { useCallback } from 'react'
import { getNotificationPrefs } from '@/lib/notifications/prefs'
import { notifySystem } from '@/lib/notifications/notify'
import { reportNotifyOutcome, type NotifyResult } from '@/lib/notifications/dispatch'
import type { NotificationItem } from '@/services/notifications/types'

const MAX_BODY_CHARS = 240

/**
 * Dispara a notificação do SO para uma notification do inbox e **devolve o
 * motivo** — quem decide o toast in-app é o chamador, a partir do resultado
 * (D-2), não da visibilidade da aba.
 *
 * **Desde a Task 19 este hook não tem porta própria além do `kind`.** Antes ele
 * repetia, com as próprias mãos, o gate de `browserEnabled` e um gate de
 * `document.hidden`; hoje delega a [[../../lib/notifications/notify]], a mesma
 * porta dos produtores de chat e de canal. A razão é a que a Task 19 chama de
 * "duas realidades": com a regra de foco escrita em três lugares, bastaria um
 * deles ficar para trás para dois avisos do mesmo app discordarem sobre onde
 * aparecer. O que sobra aqui é o que é só do inbox — a pref por `kind`, o corte
 * do corpo, o `requireInteraction` do crítico e o destino do clique.
 *
 * O clique não é tratado aqui: quem foca a janela e navega é o `public/sw.js`,
 * a partir de `data.url`. Notificação criada por service worker sobrevive à aba
 * que a criou, então `onclick` no cliente não serviria.
 */
export function useBrowserNotificationDispatcher() {
  return useCallback(async (item: NotificationItem): Promise<NotifyResult> => {
    // O único gate que a porta compartilhada não sabe aplicar: ela não conhece
    // `kind`. `critical` nunca cai aqui — a leitura das prefs força `browser:
    // true` para ele (`prefs.ts`), esteja o que estiver no storage.
    if (!getNotificationPrefs().byKind[item.kind]?.browser) {
      const resultado: NotifyResult = { outcome: 'disabled-by-pref' }
      reportNotifyOutcome(resultado, 'inbox')
      return resultado
    }

    return notifySystem(
      {
        title: item.title,
        body: item.body?.slice(0, MAX_BODY_CHARS) ?? '',
        // tag = id: o mesmo item nunca empilha duas vezes na bandeja (reentrega do
        // poll é idempotente).
        tag: item.id,
        url: `/notifications/${item.id}`,
        ...(item.kind === 'critical' ? { requireInteraction: true } : {}),
      },
      'inbox',
      // O ÚNICO furo da regra de foco no repo, e ele é do crítico: urgência não
      // espera o operador tirar o foco da tela. Não fura `browserEnabled` — ver
      // `NotifySystemOptions`.
      { ignorarFoco: item.kind === 'critical' },
    )
  }, [])
}
